import React from 'react';

export interface CdrRowItemProps {
    rawGroup: any[];
    globalExtNames: Record<string, string>;
    cnamMap: Record<string, string>;
    crm?: any;
    expandedCdrId: any;
    toggleCdrDetails: (primary: any) => void;
    expandedMissedSegments: Record<string, boolean>;
    setExpandedMissedSegments: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
    transcriptionExistsMap: Record<string, boolean>;
    serverId: string;
    audioDownloadUrlPrefix: string;
    highlightedId: string | null;
    playingFileKey: string | null;
    isBuffering: boolean;
    audioProgress: number;
    togglePlay: (url: string) => void;
    openTranscriptionModal: (filename: string, r2Key: string, audioUrl: string, txtUrl: string) => void;
    formatSeconds: (sec: number | string) => string;
    isLight: boolean;
    textColor: string;
    mutedColor: string;
    borderColor: string;
    bgExpanded: string;
}

export function CdrRowItem({
    rawGroup,
    globalExtNames,
    cnamMap,
    crm,
    expandedCdrId,
    toggleCdrDetails,
    expandedMissedSegments,
    setExpandedMissedSegments,
    transcriptionExistsMap,
    serverId,
    audioDownloadUrlPrefix,
    highlightedId,
    playingFileKey,
    isBuffering,
    audioProgress,
    togglePlay,
    openTranscriptionModal,
    formatSeconds,
    isLight,
    textColor,
    mutedColor,
    borderColor,
    bgExpanded,
}: CdrRowItemProps) {
    const seenKeys = new Set<string>();
    const group = rawGroup.filter((c: any) => {
        const key = `${c.calldate}-${c.src}-${c.dst}-${c.lastapp}-${c.dstchannel}-${c.billsec}`;
        if (seenKeys.has(key)) return false;
        seenKeys.add(key);
        return true;
    });

    let extNames: Record<string, string> = { ...globalExtNames };
    group.forEach((c: any) => {
        if (c.cnum && c.cnam && c.cnam !== 'unknown' && c.cnam !== c.cnum) extNames[c.cnum] = c.cnam;
        if (c.src && c.cnam && c.cnam !== 'unknown' && c.cnam !== c.src) extNames[c.src] = c.cnam;
        if (c.dst && c.dst_cnam && c.dst_cnam !== 'unknown' && c.dst_cnam !== c.dst) extNames[c.dst] = c.dst_cnam;
        if (c.clid) {
            const match = c.clid.match(/^"?([^"]+)"?\s*<([^>]+)>$/);
            if (match && match[1] && match[1] !== 'unknown' && match[1] !== match[2]) {
                extNames[match[2]] = match[1];
            }
        }
    });

    const originalPrimary = group[0];
    let primary = { ...originalPrimary };

    if (extNames[primary.src]) {
        primary.cnam = extNames[primary.src];
    } else if (primary.clid) {
        const match = primary.clid.match(/^"?([^"]+)"?\s*<([^>]+)>$/);
        if (match && match[1] && match[1] !== 'unknown' && match[1] !== match[2]) {
            primary.cnam = match[1];
        }
    }

    if (extNames[primary.dst]) {
        primary.dst_cnam = extNames[primary.dst];
    }

    const isExternal = (num: string) => {
        if (!num) return false;
        if (num.toLowerCase() === 'anonymous' || num.toLowerCase() === 'unknown') return true;
        const digits = num.replace(/\D/g, '');
        return digits.length >= 7 || num.startsWith('+');
    };

    if (group.length > 1) {
        const answeredSegments = group.filter((c: any) => c.disposition === 'ANSWERED');
        const answeredSegment = answeredSegments.length > 0 ? answeredSegments[answeredSegments.length - 1] : undefined;
        if (answeredSegment) {
            primary.disposition = 'ANSWERED';

            let answeredBy = answeredSegment.dstchannel || '';
            let sipMatch = answeredBy.match(/(?:SIP|PJSIP)\/(\d+)-/);
            if (sipMatch) {
                answeredBy = sipMatch[1];
            } else {
                let localMatch = answeredBy.match(/Local\/(?:FMGL-)?(\d+)/);
                if (localMatch) {
                    answeredBy = localMatch[1];
                } else {
                    answeredBy = answeredBy.split('-')[0].replace(/^(?:PJSIP|SIP|Local)\//, '');
                }
            }

            if (answeredBy && answeredBy !== answeredSegment.dst && !isExternal(answeredSegment.dst)) {
                primary.dst = answeredBy;
                primary.dst_cnam = extNames[answeredBy] || answeredSegment.dst_cnam;
            } else {
                primary.dst = answeredSegment.dst;
                primary.dst_cnam = extNames[answeredSegment.dst] || answeredSegment.dst_cnam;
            }

            primary.billsec = answeredSegments.reduce((total: number, seg: any) => total + (Number(seg.billsec) || 0), 0);
            primary.lastapp = answeredSegment.lastapp;
        }
    }

    const findDid = () => {
        for (const c of group) {
            if (c.did && String(c.did).trim().length > 0) return String(c.did).trim();
            if (c.recordingfile) {
                const m = String(c.recordingfile).match(/^in-([+0-9]+)-/);
                if (m && m[1]) return m[1];
            }
        }
        if (primary.recordingfile) {
            const m = String(primary.recordingfile).match(/^in-([+0-9]+)-/);
            if (m && m[1]) return m[1];
        }
        return '';
    };

    const didNum = findDid();

    if (primary.dst === 's') {
        const isVm = primary.lastapp === 'VoiceMail' || group.some((c: any) => c.lastapp === 'VoiceMail');
        const isPlayback = primary.lastapp === 'Playback' || group.some((c: any) => c.lastapp === 'Playback');
        const isQueue = primary.lastapp === 'Queue' || group.some((c: any) => c.lastapp === 'Queue');
        const isIvr = (primary.dcontext && primary.dcontext.includes('ivr')) || primary.lastapp === 'BackGround' || group.some((c: any) => c.lastapp === 'BackGround' || (c.dcontext && c.dcontext.includes('ivr')));

        let label = 'Auto-Attendant';
        if (isVm) label = 'Voicemail';
        else if (isPlayback) label = 'Announcement';
        else if (isQueue) label = 'Queue';
        else if (isIvr) label = 'Auto-Attendant';

        primary.displayDstName = label;
        primary.displayDstNumber = didNum || 'Start';
    } else if (/^[0-9\*#]$/.test(primary.dst)) {
        const legWithTarget = group.find((c: any) => c.dst && c.dst !== primary.dst && c.dst !== 's' && c.dst.length > 1);
        const targetDst = legWithTarget ? legWithTarget.dst : '';
        const targetName = targetDst ? (extNames[targetDst] || cnamMap[targetDst]) : '';

        if (targetDst) {
            if (targetName) {
                primary.displayDstName = `Option ${primary.dst} → ${targetName}`;
            } else if (targetDst.length >= 3 && targetDst.length <= 4) {
                primary.displayDstName = `Option ${primary.dst} → Ring Group ${targetDst}`;
            } else {
                primary.displayDstName = `Option ${primary.dst} → ${targetDst}`;
            }
            primary.displayDstNumber = targetDst;
        } else {
            primary.displayDstName = `IVR Option ${primary.dst}`;
            primary.displayDstNumber = didNum || `Option ${primary.dst}`;
        }
    } else if (primary.dst.startsWith('*') && primary.dst.length > 1) {
        if (primary.dst === '*97' || primary.dst === '*98') {
            primary.displayDstName = 'Voicemail Access';
        }
    }

    const isExpanded = expandedCdrId === originalPrimary.id;
    const groupId = originalPrimary.linkedid || originalPrimary.id;
    const isMissedExpanded = expandedMissedSegments[groupId];

    let renderedGroup = [...group];
    let missedCount = 0;

    if (group.length > 1) {
        let lastAnsweredOrFinalIdx = group.length - 1;
        for (let k = group.length - 1; k >= 0; k--) {
            if (group[k].disposition === 'ANSWERED') { lastAnsweredOrFinalIdx = k; break; }
        }

        for (let k = 0; k < group.length; k++) {
            if (k !== lastAnsweredOrFinalIdx && group[k].disposition !== 'ANSWERED') {
                missedCount++;
            }
        }

        if (!isMissedExpanded && missedCount > 0) {
            const newRendered = [];
            for (let k = 0; k < group.length; k++) {
                if (k === lastAnsweredOrFinalIdx || group[k].disposition === 'ANSWERED') {
                    newRendered.push(group[k]);
                }
            }
            renderedGroup = newRendered;
        }
    }

    const srcNormalized = (primary.cnum || primary.src || '').toString();
    const dstNormalized = (primary.dst || '').toString();
    let callDirection = 'internal';
    const hasDid = !!(primary.did && primary.did.trim() !== '');

    if (hasDid) {
        callDirection = 'inbound';
    } else if (isExternal(dstNormalized)) {
        callDirection = 'outbound';
    } else if (isExternal(srcNormalized)) {
        callDirection = 'inbound';
    } else {
        callDirection = 'internal';
    }

    let currentDepth = 0;
    let lastDst = renderedGroup[0]?.dst;
    const renderedNodes = renderedGroup.map((cdr: any, idx: number) => {
        if (idx > 0 && cdr.dst !== lastDst) {
            currentDepth++;
            lastDst = cdr.dst;
        }
        return { cdr, depth: currentDepth };
    });

    let hasHumanAnswer = false;
    let hasSystem = false;
    let hasVoicemail = false;
    const systemApps = ['voicemail', 'ivr', 'background', 'playback', 'announce', 'record', 'hangup', 'congestion', 'busy'];

    for (const cdr of group) {
        const isSysApp = systemApps.some(app => cdr.lastapp?.toLowerCase().includes(app)) || (cdr.dst && cdr.dst.startsWith('*'));
        const isVm = cdr.lastapp?.toLowerCase().includes('voicemail');
        if (cdr.disposition === 'ANSWERED') {
            if (isSysApp) {
                hasSystem = true;
                if (isVm) hasVoicemail = true;
            } else {
                hasHumanAnswer = true;
            }
        }
    }

    let callStatuses: string[] = [];
    let isMissed = false;
    let isNoAnswer = false;

    if (!hasHumanAnswer && !hasVoicemail) {
        if (callDirection === 'inbound') {
            isMissed = true;
        } else {
            if (!hasSystem) isNoAnswer = true;
        }
    }

    if (hasSystem) callStatuses.push('system');
    if (hasHumanAnswer) callStatuses.push('answered');
    if (isMissed) callStatuses.push('missed');
    if (isNoAnswer) callStatuses.push('no-answer');

    let rowRecordingUrl = '';
    let rowTxtUrl = '';
    let rowRecordingName = '';
    let rowRecordingKey = '';
    let hasTranscription = false;
    for (const node of renderedNodes) {
        if (node.cdr.recordingfile) {
            rowRecordingName = node.cdr.recordingfile.replace(/\.wav$/i, '.mp3');
            let yr = '';
            let mo = '';
            let dy = '';
            if (typeof node.cdr.calldate === 'string' && node.cdr.calldate.includes('-')) {
                const datePart = node.cdr.calldate.split(' ')[0];
                const parts = datePart.split('-');
                if (parts.length >= 3) {
                    yr = parts[0];
                    mo = parts[1];
                    dy = parts[2];
                }
            }
            if (!yr) {
                let dStr = node.cdr.calldate;
                if (typeof dStr === 'string' && dStr.includes(' ')) dStr = dStr.replace(' ', 'T');
                const dt = new Date(dStr);
                yr = dt.getFullYear().toString();
                mo = (dt.getMonth() + 1).toString().padStart(2, '0');
                dy = dt.getDate().toString().padStart(2, '0');
            }
            rowRecordingKey = `audio/recordings/${yr}/${mo}/${dy}/${rowRecordingName}`;
            const txtKey = rowRecordingKey.replace(/\.(mp3|wav)$/, '.txt');
            hasTranscription = !!transcriptionExistsMap[`pbx-${serverId}/${txtKey}`] || !!transcriptionExistsMap[txtKey];
            rowRecordingUrl = `${audioDownloadUrlPrefix}&key=${encodeURIComponent(rowRecordingKey)}`;
            rowTxtUrl = `${audioDownloadUrlPrefix}&key=${encodeURIComponent(txtKey)}`;
            break;
        }
    }

    const isHighlighted = highlightedId && (
        primary.id === highlightedId ||
        primary.uniqueid === highlightedId ||
        primary.call_group_id === highlightedId ||
        originalPrimary.linkedid === highlightedId
    );

    const renderCallerDisplay = (phone: string, rawCnam?: string) => {
        const rawName = cnamMap[phone] || (rawCnam && rawCnam !== 'Unknown' ? rawCnam : phone);
        const dictName = (rawName || '').replace(/[^a-zA-Z0-9\s\*\#\+]/g, ' ').replace(/\s+/g, ' ').trim();
        const parts = dictName.split(' ');
        const first = parts.length > 0 ? parts[0] : '';
        const last = parts.length > 1 ? parts.slice(1).join(' ') : '';
        let namePart = dictName || '-';
        if (namePart !== '-') {
            if (crm?.displayFormat === 'first_last_brackets') namePart = `${first} ${last}`.trim();
            else if (crm?.displayFormat === 'last_first_brackets') namePart = last ? `${last}, ${first}` : first;
        }
        let digits = phone.replace(/\D/g, '');
        if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
        const formattedPhone = digits.length >= 10 ? `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}` : phone;

        return { namePart, formattedPhone };
    };

    const renderCalleeDisplay = () => {
        if (primary.displayDstName) {
            const phone = primary.displayDstNumber || primary.dst;
            let digits = phone.replace(/\D/g, '');
            if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
            const formattedPhone = digits.length >= 10 ? `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}` : phone;
            return { namePart: primary.displayDstName, formattedPhone };
        }
        return renderCallerDisplay(primary.dst, primary.dst_cnam);
    };

    return (
        <div style={{ 
            position: 'relative', 
            background: isHighlighted ? (isLight ? '#eff6ff' : 'rgba(59, 130, 246, 0.15)') : (isLight ? 'white' : 'rgba(255,255,255,0.03)'), 
            borderRadius: '8px', 
            border: `1px solid ${isHighlighted ? '#3b82f6' : borderColor}`, 
            overflow: 'hidden',
            transition: 'background-color 0.4s ease, border-color 0.4s ease'
        }}>
            <div 
                style={{ padding: '0.6rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1.5rem', background: isExpanded ? bgExpanded : 'transparent', transition: 'background 0.2s' }}
            >
                <div style={{ width: '210px', display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>
                    <div style={{ color: mutedColor, fontSize: '0.9rem', display: 'flex', flexDirection: 'column', minWidth: '90px' }}>
                        {(() => {
                            const d = new Date(primary.calldate);
                            const timeStr = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
                            const dateStr = `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')}/${d.getFullYear()}`;
                            return (
                                <>
                                    <span style={{ fontWeight: 600, color: textColor }}>{timeStr}</span>
                                    <span style={{ fontSize: '0.8rem' }}>{dateStr}</span>
                                    <span style={{ fontSize: '0.8rem', color: primary.disposition === 'ANSWERED' ? '#34d399' : mutedColor, marginTop: '0.1rem' }}>
                                        {formatSeconds(primary.billsec)}
                                    </span>
                                    {primary.is_archived && (
                                        <span style={{
                                            fontSize: '0.65rem',
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            padding: '0.1rem 0.35rem',
                                            borderRadius: '3px',
                                            background: isLight ? 'rgba(2, 132, 199, 0.12)' : 'rgba(56, 189, 248, 0.2)',
                                            color: isLight ? '#0284c7' : '#38bdf8',
                                            border: isLight ? '1px solid rgba(2, 132, 199, 0.3)' : '1px solid rgba(56, 189, 248, 0.35)',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '0.2rem',
                                            width: 'fit-content',
                                            marginTop: '0.2rem'
                                        }}>
                                            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 8v13H3V8"></path><path d="M1 3h22v5H1z"></path><path d="M10 12h4"></path></svg>
                                            ARCHIVE
                                        </span>
                                    )}
                                </>
                            );
                        })()}
                    </div>
                    <div 
                        style={{ display: 'flex', gap: '0.25rem', alignItems: 'center', cursor: 'pointer' }}
                        onClick={() => toggleCdrDetails(primary)}
                        title="Expand Call Details"
                    >
                        {(() => {
                            const renderedIcons = [];
                            if (callDirection === 'inbound') renderedIcons.push(<div key="in" title="Inbound Call" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '6px', background: 'rgba(168, 85, 247, 0.15)', flexShrink: 0 }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={isLight ? "#9333ea" : "#a855f7"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 2 16 8 22 8"></polyline><line x1="22" y1="2" x2="16" y2="8"></line><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg></div>);
                            else if (callDirection === 'outbound') renderedIcons.push(<div key="out" title="Outbound Call" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '6px', background: 'rgba(59, 130, 246, 0.15)', flexShrink: 0 }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={isLight ? "#2563eb" : "#3b82f6"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 7 23 1 17 1"></polyline><line x1="16" y1="8" x2="23" y2="1"></line><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg></div>);
                            else if (callDirection === 'internal') renderedIcons.push(<div key="int" title="Internal Call" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '6px', background: isLight ? 'rgba(234, 179, 8, 0.2)' : 'rgba(234, 179, 8, 0.15)', flexShrink: 0 }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={isLight ? "#d97706" : "#eab308"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="17 11 21 7 17 3"></polyline><line x1="21" y1="7" x2="9" y2="7"></line><polyline points="7 21 3 17 7 13"></polyline><line x1="3" y1="17" x2="15" y2="17"></line></svg></div>);

                            if (callStatuses.includes('answered')) renderedIcons.push(<div key="ans" title="Answered" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '6px', background: 'rgba(52, 211, 153, 0.15)', flexShrink: 0 }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={isLight ? "#059669" : "#34d399"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg></div>);
                            if (callStatuses.includes('missed')) renderedIcons.push(<div key="miss" title="Missed" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '6px', background: 'rgba(239, 68, 68, 0.15)', flexShrink: 0 }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={isLight ? "#dc2626" : "#f87171"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path><line x1="22" y1="2" x2="16" y2="8"></line><line x1="16" y1="2" x2="22" y2="8"></line></svg></div>);
                            if (callStatuses.includes('no-answer')) renderedIcons.push(<div key="noans" title="No Answer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '6px', background: 'rgba(156, 163, 175, 0.15)', flexShrink: 0 }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={isLight ? "#6b7280" : "#9ca3af"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91"></path><line x1="22" y1="2" x2="2" y2="22"></line></svg></div>);
                            if (callStatuses.includes('system')) renderedIcons.push(<div key="sys" title="System Answered" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '6px', background: isLight ? 'rgba(234, 179, 8, 0.2)' : 'rgba(234, 179, 8, 0.15)', flexShrink: 0 }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={isLight ? "#d97706" : "#eab308"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="8" rx="2" ry="2"></rect><rect x="2" y="14" width="20" height="8" rx="2" ry="2"></rect><line x1="6" y1="6" x2="6.01" y2="6"></line><line x1="6" y1="18" x2="6.01" y2="18"></line></svg></div>);

                            return renderedIcons.flatMap((icon, idx) => {
                                const elements = [icon];
                                if (idx < renderedIcons.length - 1) {
                                    elements.push(<div key={`div-${idx}`} style={{ width: '1px', height: '16px', background: isLight ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.15)', borderRadius: '1px' }}></div>);
                                }
                                return elements;
                            });
                        })()}
                    </div>
                </div>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '1rem', minWidth: 0 }}>
                    {callDirection === 'outbound' ? (
                        <>
                            {(() => {
                                const rawName = primary.cnam && primary.cnam !== 'Unknown' ? primary.cnam : primary.src;
                                const dictName = (rawName || '').replace(/[^a-zA-Z0-9\s\*\#\+]/g, ' ').replace(/\s+/g, ' ').trim();
                                const parts = dictName.split(' ');
                                const first = parts.length > 0 ? parts[0] : '';
                                const last = parts.length > 1 ? parts.slice(1).join(' ') : '';
                                let namePart = dictName || '-';
                                if (namePart !== '-') {
                                    if (crm?.displayFormat === 'first_last_brackets') namePart = `${first} ${last}`.trim();
                                    else if (crm?.displayFormat === 'last_first_brackets') namePart = last ? `${last}, ${first}` : first;
                                }
                                const phone = primary.src;
                                let digits = phone.replace(/\D/g, '');
                                if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
                                const formattedPhone = digits.length >= 10 ? `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}` : phone;

                                return (
                                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                        <div style={{ fontWeight: 600, color: textColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{namePart}</div>
                                        <div style={{ color: mutedColor, fontSize: '0.8rem', marginTop: '0.1rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{formattedPhone}</div>
                                    </div>
                                );
                            })()}
                            
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={mutedColor} strokeWidth="2" style={{ flexShrink: 0 }}><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                            
                            {(() => {
                                const callee = renderCalleeDisplay();
                                return (
                                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                        <div style={{ fontWeight: 600, color: textColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{callee.namePart}</div>
                                        <div style={{ color: mutedColor, fontSize: '0.8rem', marginTop: '0.1rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{callee.formattedPhone}</div>
                                    </div>
                                );
                            })()}
                        </>
                    ) : (
                        <>
                            {(() => {
                                const caller = renderCallerDisplay(primary.src, primary.cnam);
                                return (
                                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                        <div style={{ fontWeight: 600, color: textColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{caller.namePart}</div>
                                        <div style={{ color: mutedColor, fontSize: '0.8rem', marginTop: '0.1rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{caller.formattedPhone}</div>
                                    </div>
                                );
                            })()}
                            
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={mutedColor} strokeWidth="2" style={{ flexShrink: 0 }}><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                            
                            {(() => {
                                const callee = renderCalleeDisplay();
                                return (
                                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                        <div style={{ fontWeight: 600, color: textColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{callee.namePart}</div>
                                        <div style={{ color: mutedColor, fontSize: '0.8rem', marginTop: '0.1rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{callee.formattedPhone}</div>
                                    </div>
                                );
                            })()}
                        </>
                    )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginLeft: 'auto' }}>
                    {rowRecordingName && (
                        <>
                            <button 
                                onClick={(e) => { e.stopPropagation(); togglePlay(rowRecordingUrl); }} 
                                title={playingFileKey === rowRecordingUrl ? "Stop Audio" : "Play Audio"} 
                                style={{ background: 'transparent', border: 'none', color: playingFileKey === rowRecordingUrl ? '#ef4444' : '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: '0.25rem', borderRadius: '4px' }}
                            >
                                {playingFileKey === rowRecordingUrl ? (
                                    isBuffering ? (
                                        <svg style={{ width: '14px', height: '14px', minWidth: '14px', display: 'block', animation: 'spin 1s linear infinite' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={playingFileKey === rowRecordingUrl ? '#ef4444' : '#10b981'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="2" x2="12" y2="6"></line><line x1="12" y1="18" x2="12" y2="22"></line><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line><line x1="2" y1="12" x2="6" y2="12"></line><line x1="18" y1="12" x2="22" y2="12"></line><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line></svg>
                                    ) : (
                                        <svg style={{ width: '14px', height: '14px', minWidth: '14px', display: 'block' }} width="14" height="14" viewBox="0 0 24 24" fill={playingFileKey === rowRecordingUrl ? '#ef4444' : '#10b981'} stroke={playingFileKey === rowRecordingUrl ? '#ef4444' : '#10b981'} strokeWidth="2"><rect x="6" y="4" width="4" height="16"></rect><rect x="14" y="4" width="4" height="16"></rect></svg>
                                    )
                                ) : (
                                    <svg style={{ width: '14px', height: '14px', minWidth: '14px', display: 'block' }} width="14" height="14" viewBox="0 0 24 24" fill={playingFileKey === rowRecordingUrl ? '#ef4444' : '#10b981'} stroke={playingFileKey === rowRecordingUrl ? '#ef4444' : '#10b981'} strokeWidth="2"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
                                )}
                            </button>
                            <button 
                                onClick={(e) => { e.stopPropagation(); window.open(rowRecordingUrl + (rowRecordingUrl.includes('?') ? '&' : '?') + 'download=true', '_blank'); }} 
                                title="Download Audio" 
                                style={{ background: 'transparent', border: 'none', color: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: '0.25rem', borderRadius: '4px' }}
                            >
                                <svg style={{ width: '14px', height: '14px', minWidth: '14px', display: 'block' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                            </button>
                            <button 
                                onClick={(e) => { e.stopPropagation(); openTranscriptionModal(rowRecordingName, rowRecordingKey, rowRecordingUrl, rowTxtUrl); }} 
                                title={hasTranscription ? "Transcription Available / Re-transcribe" : "Transcribe Audio"} 
                                style={{ background: 'transparent', border: 'none', color: hasTranscription ? '#10b981' : '#8b5cf6', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: '0.25rem', borderRadius: '4px' }}
                            >
                                <svg style={{ width: '14px', height: '14px', minWidth: '14px', display: 'block' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={hasTranscription ? '#10b981' : '#8b5cf6'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"></path><path d="M19 10v2a7 7 0 0 1-14 0v-2"></path><line x1="12" y1="19" x2="12" y2="23"></line><line x1="8" y1="23" x2="16" y2="23"></line></svg>
                            </button>
                        </>
                    )}
                </div>
            </div>
            
            {playingFileKey === rowRecordingUrl && (
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '4px', background: 'rgba(0,0,0,0.1)' }}>
                    <div style={{ height: '100%', background: '#10b981', width: `${audioProgress}%`, transition: 'width 0.1s linear' }} />
                </div>
            )}

            {isExpanded && (
                <div style={{ padding: '1rem 1.5rem', borderTop: `1px solid ${borderColor}`, background: isLight ? 'rgba(0,0,0,0.02)' : 'rgba(0,0,0,0.2)' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {rowRecordingName && (
                            <div style={{ color: mutedColor, fontSize: '0.8rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem', paddingLeft: '0.5rem' }}>
                                <svg style={{ width: '14px', height: '14px', display: 'block' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                                Recorded File: {rowRecordingName}
                            </div>
                        )}
                        {renderedNodes.map(({cdr, depth}: any, j: number) => {
                            const srcDisplay = extNames[cdr.src] ? `${extNames[cdr.src]} (${cdr.src})` : cdr.src;
                            const rawDst = extNames[cdr.dst] ? `${extNames[cdr.dst]} (${cdr.dst})` : cdr.dst;
                            const dstDisplay = cdr.dst === 's' ? 'Auto-Attendant' : (/^[0-9\*#]$/.test(cdr.dst) ? `Option ${cdr.dst}` : rawDst);
                            
                            let extDisplay = '';
                            if (cdr.dstchannel) {
                                let sipMatch = cdr.dstchannel.match(/(?:SIP|PJSIP)\/(\d+)-/);
                                let localMatch = cdr.dstchannel.match(/Local\/(?:FMGL-)?(\d+)/);
                                let extNum = sipMatch ? sipMatch[1] : (localMatch ? localMatch[1] : null);
                                
                                if (extNum && extNum !== cdr.dst) {
                                    let extName = extNames[extNum] ? `${extNames[extNum]} (${extNum})` : extNum;
                                    extDisplay = ` -> ${extName}`;
                                }
                            }
                            
                            return (
                                <div key={j} style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '0.9rem', padding: '0.6rem 1rem', marginLeft: `${depth * 2}rem`, background: cdr.disposition === 'ANSWERED' ? (isLight ? 'rgba(56, 189, 248, 0.1)' : 'rgba(56, 189, 248, 0.05)') : 'transparent', border: `1px solid ${cdr.disposition === 'ANSWERED' ? (isLight ? '#38bdf8' : 'rgba(56, 189, 248, 0.15)') : borderColor}`, borderRadius: '6px' }}>
                                    <div style={{ flex: 1, color: textColor }}>
                                        {new Date(cdr.calldate).toLocaleTimeString()} - {srcDisplay} {'->'} {dstDisplay}{extDisplay}
                                        <span style={{ color: mutedColor, marginLeft: '1rem' }}>App: {cdr.lastapp}</span>
                                        <span style={{ color: mutedColor, marginLeft: '1rem', fontSize: '0.8rem' }}>Ch: {cdr.dstchannel || cdr.channel}</span>
                                        <span style={{ color: cdr.disposition === 'ANSWERED' ? '#34d399' : mutedColor, marginLeft: '1rem', fontSize: '0.8rem', fontWeight: 600 }}>{formatSeconds(cdr.billsec)}</span>
                                    </div>
                                </div>
                            );
                        })}

                        {missedCount > 0 && !isMissedExpanded && (
                            <button 
                                onClick={() => setExpandedMissedSegments(prev => ({ ...prev, [groupId]: true }))}
                                style={{ background: 'none', border: 'none', color: '#38bdf8', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, textAlign: 'left', padding: '0.5rem 1rem' }}
                            >
                                + Show {missedCount} Unanswered Attempt{missedCount > 1 ? 's' : ''}
                            </button>
                        )}
                        {missedCount > 0 && isMissedExpanded && (
                            <button 
                                onClick={() => setExpandedMissedSegments(prev => ({ ...prev, [groupId]: false }))}
                                style={{ background: 'none', border: 'none', color: mutedColor, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, textAlign: 'left', padding: '0.5rem 1rem' }}
                            >
                                - Hide Unanswered Attempt{missedCount > 1 ? 's' : ''}
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
